/**
 * The `/database` section — RO-3-11 (#44304), part 1 of 4.
 *
 * Port of eight of the sixteen `excellent/app/database/**\/page.tsx` wrappers:
 * the overview, the two schema pages, migrations, the SQL editor, fleet sync,
 * the import hub, and the assistant redirect. The editor, objects and
 * queue/views pages are the three sibling files (`database-editor.tsx`,
 * `database-objects.tsx`, `database-queues.tsx`) — split by surface, not by
 * size, so each file reads as one job.
 *
 * ── What the port actually changes ──
 *
 * Every page here was a Next SERVER component that called
 * `@excellent/core/server` inline (`connectionInfo()`, `listObjects()`,
 * `relationships()`, `tableDetail()`, a raw `getDb().prepare(...)`). None of that
 * can run in a browser, so the recipe for each is the same three lines:
 *
 *   `connectionInfo()` etc.  ->  `useApi(schema.connection, {})`
 *   `next/link`              ->  the router seam's `RouterLink`
 *   `export const metadata`  ->  dropped (no Next head; the shell owns the title)
 *
 * The BODY of each page is otherwise unchanged — same `<PageHeader>`, same
 * `<InfoHint>` copy, same block composition, same class names — because this is a
 * transport change (PRD §4.1) and a page that looks different after the port is a
 * defect. Where a `@excellent/ui` block already self-fetched (`RetentionHealthCard`,
 * `IntakeBatchesCard`, `ChangelogTable`, `SqlEditor`, `DatabaseAssistant`,
 * `BackupButton`), it is dropped in untouched and works here with no change at
 * all — RO-4-02 had already moved those blocks onto the router seam.
 *
 * ── Reads this item had to write, and why that was not optional ──
 *
 * Seven of the sixteen pages had NO route and NO server action to convert, so
 * neither RO-1-05 nor RO-2-03 left anything for the port to call. Two of them are
 * in this file: `/database/migrations` (an inline `_migrations` query) and
 * `/database/schema/$object` (the semantic registry's prose). `shared-render.md`
 * §5 is explicit that feeding such a page from a `fetch()` is a rail violation
 * rather than a shortcut, so `schema.migrations` and `schema.meaning` were added
 * to the contract and mounted on this namespace. The other five are documented in
 * the sibling files and in `server/routes/database.ts`'s RO-3-11 block.
 *
 * ── Two holes, stated where they are missing ──
 *
 * 1. `<DropIsland>` (`app/database/_drop-island.tsx`, 231 LOC) is NOT on the
 *    overview page. It imports `bandForSnapshot` / `batchDrillHref` from
 *    `app/settings/integrations/sources/new/_intake-states.ts` — a pure helper
 *    homed inside the SETTINGS tree. This host may not import from
 *    `excellent/app/` (PRD D6: that tree is the rollback and RO-10-09 deletes
 *    it), and re-homing the helper means editing a file RO-3-09 owns. Copying the
 *    two functions here instead would be a third home for one derivation, which
 *    is the duplication `shared-render.md` exists to prevent. So it is deferred to
 *    whoever relocates that helper. The intake LEDGER below it
 *    (`<IntakeBatchesCard>`, `packages/ui`) ports and is live, and
 *    `/database/import` gives the section a complete import surface — the missing
 *    piece is the drag-and-drop shortcut, not the capability.
 *
 * 2. `DatabaseRail` (`app/database/_rail.tsx`, 131 LOC) — the section's eight-item
 *    nav — is not ported. It floats itself into the shell's right-gutter
 *    `ContextRail` via `useProvideRailExtension`, and `client/shell/shell.tsx`
 *    declares `contextRail` as an UNFILLED SLOT (its header says so): the rail
 *    components are Next client components still importing `next/navigation`.
 *    Porting the extension without its host would register into nothing. The
 *    shell's slot is the right seam and filling it is L4's, not a page port's.
 */

import * as React from "react";
import { createRoute, redirect } from "@tanstack/react-router";
import {
  BackupButton,
  Button,
  ChangelogTable,
  InfoHint,
  IntakeBatchesCard,
  ObjectDetail,
  PageHeader,
  RetentionHealthCard,
  SchemaGraph,
  SectionHeading,
  StatStrip,
  TableInventory,
  TableSwitcher,
  formatRelativeTime,
  type StatStripItem,
} from "@excellent/ui";
import { TerminalSquare } from "@excellent/ui/icons";
import { RouterLink as Link } from "@excellent/ui/router";
import { useApi } from "@excellent/ui/api";
import { schema } from "@excellent/ui/api/contracts/database/schema";
import { rootRoute } from "./__root";
import { AppliedMigrationsTable } from "./database/applied-migrations-table";
import { SqlWorkbench } from "./database/sql-workbench";
import { ImportHub } from "./database/import-hub";
import { FleetSyncPanel } from "../shared";

/**
 * The loading / error line every page in this section shows above its body.
 *
 * One helper rather than the same two ternaries eight times, and deliberately NOT
 * a skeleton: these reads are local SQLite introspection behind a same-process
 * HTTP hop, so they resolve in single-digit milliseconds. A skeleton would flash.
 *
 * `hasData` is the caller's payload-presence flag: with the shared SWR cache a
 * revisit already holds the read, so the line must stay down while a background
 * revalidate runs rather than blink over a body that is already painted.
 */
function ReadState({
  isLoading,
  error,
  hasData,
}: {
  isLoading: boolean;
  error?: { message?: string } | null;
  hasData?: boolean;
}) {
  if (error) return <p className="type-body text-destructive">{error.message ?? String(error)}</p>;
  if (isLoading && !hasData) return <p className="type-body text-muted-foreground">Loading…</p>;
  return null;
}

// ── /database — the overview ─────────────────────────────────────────────────

function DatabaseOverview() {
  const objects = useApi(schema.objects, {});
  const connection = useApi(schema.connection, {});

  const info = connection.data;
  const projects = info?.migrations.find((m) => m.product === "excellent_projects");

  return (
    <div className="flex flex-col">
      <div className="flex flex-col gap-6 px-6 py-8 md:px-10">
        {/* Canonical page-title treatment (Issue 7/23): the standard PageHeader,
            with the `.db` path (+ the SQLite engine facts behind an (i)) as the
            subtitle and the corpus actions in the toolbar. The three toolbar
            buttons share one size + follow the button grammar (one primary, the
            rest outline) so they line up. */}
        <PageHeader
          title="Database"
          description={
            info ? (
              <span className="flex items-center gap-1.5 font-mono type-subtext text-muted-foreground">
                {info.path}
                <InfoHint label="SQLite connection facts" side="bottom" align="start">
                  <span className="block">
                    SQLite {info.sqliteVersion} · {info.journalMode.toUpperCase()}{" "}
                    journal · foreign keys {info.foreignKeys ? "on" : "off"} · page
                    size {info.pageSize} B
                    {projects ? ` · schema v${projects.version}` : ""}
                  </span>
                  <span className="mt-1 block">
                    Read-only by default — flip write mode on a table or in the SQL
                    editor to make changes.
                  </span>
                </InfoHint>
              </span>
            ) : (
              "Connection facts and the full table inventory of the local SQLite database."
            )
          }
          toolbar={
            <>
              {objects.data ? <TableSwitcher objects={objects.data} trigger="button" /> : null}
              <BackupButton label="Back up" />
              <Button size="sm" asChild>
                <Link href="/database/sql">
                  <TerminalSquare className="size-3.5" /> SQL editor
                </Link>
              </Button>
            </>
          }
        />
        <ReadState
          isLoading={objects.isLoading || connection.isLoading}
          error={objects.error ?? connection.error}
          hasData={Boolean(objects.data && connection.data)}
        />
        {/* Retention & size Health card (Data Design PRD §7.3): DB size, growth,
            and what the current retention policy would reclaim. Carries the
            standard plain SectionTitle header on top. */}
        <RetentionHealthCard />
        {/* Universal Intake ledger (INTAKE-PRD §2.8.2 / slice U-A): recent
            batches captured through file / CLI-MCP / URL. Carries its own plain
            SectionTitle header. The `<DropIsland>` that sat ABOVE this card is
            deferred — see this file's header, hole 1. */}
        <IntakeBatchesCard />
        {/* The full object inventory — the DataTable owns its own integrated
            "Objects" header, the standard table-title treatment. */}
        {objects.data && info ? <TableInventory objects={objects.data} info={info} /> : null}
      </div>
    </div>
  );
}

export const databaseIndexRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/database",
  component: DatabaseOverview,
});

// ── /database/schema — relationships + inventory ─────────────────────────────

function DatabaseSchema() {
  const rels = useApi(schema.relationships, {});
  const objects = useApi(schema.objects, {});
  const connection = useApi(schema.connection, {});

  return (
    <div className="flex flex-col">
      <div className="flex flex-col gap-8 px-6 py-8">
        <PageHeader
          title="Schema"
          description="Foreign-key relationships across the database, plus the full object inventory."
          toolbar={objects.data ? <TableSwitcher objects={objects.data} trigger="button" /> : null}
        />
        <ReadState
          isLoading={rels.isLoading || objects.isLoading || connection.isLoading}
          error={rels.error ?? objects.error ?? connection.error}
          hasData={Boolean(rels.data && objects.data && connection.data)}
        />
        {rels.data && objects.data ? (
          <section className="flex flex-col gap-3">
            <SectionHeading title="Relationships" />
            <SchemaGraph relationships={rels.data} objects={objects.data} />
          </section>
        ) : null}
        {/* The inventory is its own integrated Card (titled "Objects"). */}
        {objects.data && connection.data ? (
          <TableInventory objects={objects.data} info={connection.data} linkMode="schema" />
        ) : null}
      </div>
    </div>
  );
}

export const databaseSchemaRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/database/schema",
  component: DatabaseSchema,
});

// ── /database/schema/$object — one table's structure + semantics ─────────────

function DatabaseSchemaObject() {
  const { object } = databaseSchemaObjectRoute.useParams();
  // The Next page did `decodeURIComponent(raw)` because App Router handed it the
  // raw path segment. TanStack decodes params, so decoding again would corrupt a
  // name containing a literal `%` — one of the few places the port is NOT a
  // verbatim copy, and the reason is here rather than in a diff nobody reads.
  const detail = useApi(schema.object, { query: { name: object } });
  // Semantic registry (Data Design §5.4): table + per-column meaning, keyed to
  // the table's real columns so we never describe a column it doesn't have. The
  // keying happens server-side, in the `schema.meaning` handler.
  const semantics = useApi(schema.meaning, { query: { name: object } });

  return (
    <div className="flex flex-col">
      <div className="flex flex-col gap-8 px-6 py-8">
        <ReadState isLoading={detail.isLoading} error={detail.error} hasData={Boolean(detail.data)} />
        {detail.data ? (
          <ObjectDetail
            detail={detail.data}
            meaning={semantics.data?.meaning}
            columnMeanings={semantics.data?.columnMeanings}
          />
        ) : null}
      </div>
    </div>
  );
}

export const databaseSchemaObjectRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/database/schema/$object",
  component: DatabaseSchemaObject,
});

// ── /database/migrations — the applied ledger + the live-change audit ────────

function DatabaseMigrations() {
  const { data, error, isLoading } = useApi(schema.migrations, {});
  const rows = data?.rows ?? [];

  // The stats stay a DERIVATION over the rows, exactly as the server page had
  // them — moving them into the endpoint would create a second place the schema
  // version is computed, and two derivations can disagree.
  const stats: StatStripItem[] = React.useMemo(() => {
    const products = new Set(rows.map((r) => r.product)).size;
    const projects = rows.filter((r) => r.product === "excellent_projects");
    const currentVersion = projects.length ? Math.max(...projects.map((r) => r.version)) : null;
    const lastApplied = rows.reduce<string | null>(
      (max, r) => (max == null || r.applied_at > max ? r.applied_at : max),
      null,
    );
    // The migration stats ride the table's caption band, not a floating strip.
    return [
      {
        title: "Schema",
        value: currentVersion != null ? `v${currentVersion}` : "—",
        description: "excellent_projects",
      },
      { title: "Products", value: String(products), description: "at canonical schema" },
      { title: "Migrations", value: String(rows.length), description: "applied" },
      {
        title: "Last applied",
        value: formatRelativeTime(lastApplied),
        description: "most recent",
      },
    ];
  }, [rows]);

  return (
    <div className="flex flex-col">
      <div className="flex flex-col gap-8 px-6 py-8">
        <ReadState isLoading={isLoading} error={error} hasData={Boolean(data)} />
        {data ? <AppliedMigrationsTable rows={rows} stats={stats} /> : null}
        <ChangelogTable />
      </div>
    </div>
  );
}

export const databaseMigrationsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/database/migrations",
  component: DatabaseMigrations,
});

// ── /database/sql — the audited SQL runner ───────────────────────────────────

function DatabaseSql() {
  return (
    <div className="flex flex-col">
      <div className="flex flex-col gap-8 px-6 py-8">
        <SqlWorkbench />
      </div>
    </div>
  );
}

export const databaseSqlRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/database/sql",
  component: DatabaseSql,
});

// ── /database/sync — the fleet operator surface ──────────────────────────────
//
// `FleetSyncPanel` is imported from `client/shared/` and NOT re-ported: RO-3-00
// already ported it (origin `app/database/sync/_client.tsx`) because five root
// pages across this section and Settings reach it. Both its contracts
// (`sync.status`, `sync.now`) are mounted, so this page is live today.

function DatabaseSync() {
  return (
    <div className="flex flex-col">
      <div className="flex flex-col gap-8 px-6 py-8">
        <PageHeader
          title={
            <span className="flex items-center gap-2">
              Fleet Sync
              <InfoHint label="How fleet sync works" side="bottom" align="start">
                Each teammate runs a local Excellent that is an embedded replica
                of the owned <code className="font-mono">sqld</code> primary.
                Reads stay local-speed; writes forward to the primary and fan
                out to every node. Concurrent offline edits to the same record
                resolve last-write-wins today — field-level merge is the team
                sync plane, off by default. Surfaced divergences are reconciled
                under{" "}
                <Link className="underline" href="/database/conflicts">
                  Sync Conflicts
                </Link>
                .
              </InfoHint>
            </span>
          }
          description="Connection health, the fleet roster, and a manual sync trigger."
        />
        <FleetSyncPanel />
      </div>
    </div>
  );
}

export const databaseSyncRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/database/sync",
  component: DatabaseSync,
});

// ── /database/import — the typed-import hub ──────────────────────────────────

function DatabaseImport() {
  return (
    <div className="flex flex-col gap-4">
      <PageHeader
        title="Import data"
        description="Bring a CSV, TSV, or JSON into the data plane. Pick what the file is; columns map to the target's fields and rows land via the Schema."
      />
      <ImportHub />
    </div>
  );
}

export const databaseImportRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/database/import",
  component: DatabaseImport,
});

// ── /database/assistant — a legacy-link redirect ─────────────────────────────
//
// The Database Assistant moved into Settings → Agents → Database assistant on
// Next. That settings leaf is unmounted here (#44573 owns `/settings/agents/*`).
// #44576 retargets the interim dest to the mounted Database overview so the
// bookmark lands on real content, not the SPA not-found boundary. Restore
// `/settings/agents/database` when that sibling page ports.

export const databaseAssistantRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/database/assistant",
  beforeLoad: () => {
    throw redirect({ to: "/database", replace: true });
  },
});
